import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { exportRecordPDF, exportRecordXLSX } from '@/lib/clientExport';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Download, TrendingUp, Package, Loader2 } from 'lucide-react';
import CanAccess from '@/components/ui/CanAccess';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area, Legend,
} from 'recharts';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import FilterBar from '@/components/ui/FilterBar';
import ALCOABadge from '@/components/ui/ALCOABadge';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import ChartCard from '@/components/ui/ChartCard';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import { ChartSkeleton } from '@/components/ui/LoadingSkeleton';
import ProgressBar from '@/components/ui/ProgressBar';
import { format } from 'date-fns';

interface BatchRecord {
  id: string;
  batchCode: string;
  productName: string;
  productCode: string;
  lab: { name: string };
  status: string;
  batchSize: number;
  unit: string;
  yieldActual: number;
  yieldExpected: number;
  yieldPercent: number;
  startDate: string;
  endDate: string | null;
  releaseDate: string | null;
  operator: string;
  reviewedBy: string | null;
  deviations: number;
  remarks: string | null;
}

const MOCK_BATCHES: BatchRecord[] = [
  // 2023
  {
    id: 'mock-batch-2301', batchCode: 'BT-AMX-2301', productName: 'Amoxicillin 500mg Capsules',
    productCode: 'AMX-CAP-500', lab: { name: 'Pune Formulation Lab' }, status: 'released',
    batchSize: 200000, unit: 'units', yieldActual: 196000, yieldExpected: 200000,
    yieldPercent: 98.0, startDate: '2023-02-05', endDate: '2023-02-09',
    releaseDate: '2023-02-16', operator: 'Suresh Patil', reviewedBy: 'Dr. Anjali Mehta',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2302', batchCode: 'BT-PCM-2302', productName: 'Paracetamol 650mg ER Tablets',
    productCode: 'PCM-TAB-650', lab: { name: 'Hyderabad API Lab' }, status: 'released',
    batchSize: 800000, unit: 'tablets', yieldActual: 786400, yieldExpected: 800000,
    yieldPercent: 98.3, startDate: '2023-03-12', endDate: '2023-03-17',
    releaseDate: '2023-03-25', operator: 'Harish Reddy', reviewedBy: 'Dr. Meena Iyer',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2303', batchCode: 'BT-MET-2303', productName: 'Metformin 500mg Tablets',
    productCode: 'MET-TAB-500', lab: { name: 'Pune Formulation Lab' }, status: 'completed',
    batchSize: 600000, unit: 'tablets', yieldActual: 568200, yieldExpected: 600000,
    yieldPercent: 94.7, startDate: '2023-05-08', endDate: '2023-05-13',
    releaseDate: '2023-05-22', operator: 'Sonal Desai', reviewedBy: 'Ramesh Kulkarni',
    deviations: 1, remarks: 'Minor tablet hardness deviation noted during compression; within specification after process adjustment.',
  },
  {
    id: 'mock-batch-2304', batchCode: 'BT-IBU-2304', productName: 'Ibuprofen 400mg Tablets',
    productCode: 'IBU-TAB-400', lab: { name: 'Mumbai QC Central' }, status: 'released',
    batchSize: 500000, unit: 'tablets', yieldActual: 493500, yieldExpected: 500000,
    yieldPercent: 98.7, startDate: '2023-07-03', endDate: '2023-07-08',
    releaseDate: '2023-07-16', operator: 'Kavitha Nair', reviewedBy: 'Dr. Anjali Mehta',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2305', batchCode: 'BT-SAL-2305', productName: 'Sodium Chloride 0.9% Injection',
    productCode: 'SAL-INJ-090', lab: { name: 'Chennai Sterile Lab' }, status: 'released',
    batchSize: 12000, unit: 'vials', yieldActual: 11820, yieldExpected: 12000,
    yieldPercent: 98.5, startDate: '2023-08-14', endDate: '2023-08-16',
    releaseDate: '2023-08-28', operator: 'Arun Menon', reviewedBy: 'Priya Nair',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2306', batchCode: 'BT-ATV-2306', productName: 'Atorvastatin 10mg Tablets',
    productCode: 'ATV-TAB-010', lab: { name: 'Pune Formulation Lab' }, status: 'rejected',
    batchSize: 400000, unit: 'tablets', yieldActual: 336000, yieldExpected: 400000,
    yieldPercent: 84.0, startDate: '2023-10-05', endDate: '2023-10-10',
    releaseDate: null, operator: 'Suresh Patil', reviewedBy: 'Dr. Anjali Mehta',
    deviations: 3, remarks: 'Batch rejected: dissolution failure at 45-min timepoint for Q80 specification. Root cause traced to binder over-granulation. CAPA initiated. Batch destroyed per SOP-QA-011.',
  },
  // 2024
  {
    id: 'mock-batch-2401', batchCode: 'BT-CIP-2401', productName: 'Ciprofloxacin 250mg Tablets',
    productCode: 'CIP-TAB-250', lab: { name: 'Mumbai QC Central' }, status: 'released',
    batchSize: 500000, unit: 'tablets', yieldActual: 494500, yieldExpected: 500000,
    yieldPercent: 98.9, startDate: '2024-01-08', endDate: '2024-01-13',
    releaseDate: '2024-01-21', operator: 'Kavitha Nair', reviewedBy: 'Dr. Anjali Mehta',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2402', batchCode: 'BT-PRD-2402', productName: 'Prednisolone 5mg Tablets',
    productCode: 'PRD-TAB-005', lab: { name: 'Pune Formulation Lab' }, status: 'completed',
    batchSize: 300000, unit: 'tablets', yieldActual: 291900, yieldExpected: 300000,
    yieldPercent: 97.3, startDate: '2024-02-19', endDate: '2024-02-23',
    releaseDate: '2024-03-04', operator: 'Sonal Desai', reviewedBy: 'Ramesh Kulkarni',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2403', batchCode: 'BT-AZI-2403', productName: 'Azithromycin 500mg Tablets',
    productCode: 'AZI-TAB-500', lab: { name: 'Hyderabad API Lab' }, status: 'released',
    batchSize: 250000, unit: 'tablets', yieldActual: 248250, yieldExpected: 250000,
    yieldPercent: 99.3, startDate: '2024-04-01', endDate: '2024-04-05',
    releaseDate: '2024-04-14', operator: 'Harish Reddy', reviewedBy: 'Dr. Meena Iyer',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2404', batchCode: 'BT-PAN-2404', productName: 'Pantoprazole 40mg Tablets',
    productCode: 'PAN-TAB-040', lab: { name: 'Pune Formulation Lab' }, status: 'released',
    batchSize: 350000, unit: 'tablets', yieldActual: 343700, yieldExpected: 350000,
    yieldPercent: 98.2, startDate: '2024-06-10', endDate: '2024-06-15',
    releaseDate: '2024-06-24', operator: 'Arjun Pillai', reviewedBy: 'Dr. Anjali Mehta',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2405', batchCode: 'BT-SAL-2405', productName: 'Sodium Chloride 0.9% Injection',
    productCode: 'SAL-INJ-090', lab: { name: 'Chennai Sterile Lab' }, status: 'quarantine',
    batchSize: 15000, unit: 'vials', yieldActual: 14700, yieldExpected: 15000,
    yieldPercent: 98.0, startDate: '2024-07-22', endDate: '2024-07-24',
    releaseDate: null, operator: 'Arun Menon', reviewedBy: 'Priya Nair',
    deviations: 2, remarks: 'Batch quarantined: visible particulate found during 100% visual inspection. Particle identified as glass lamellae. Investigation ongoing; risk assessment for patient impact in progress.',
  },
  {
    id: 'mock-batch-2406', batchCode: 'BT-MET-2406', productName: 'Metformin 500mg Tablets',
    productCode: 'MET-TAB-500', lab: { name: 'Pune Formulation Lab' }, status: 'released',
    batchSize: 700000, unit: 'tablets', yieldActual: 693000, yieldExpected: 700000,
    yieldPercent: 99.0, startDate: '2024-09-03', endDate: '2024-09-08',
    releaseDate: '2024-09-18', operator: 'Deepak Rao', reviewedBy: 'Ramesh Kulkarni',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2407', batchCode: 'BT-IBU-2407', productName: 'Ibuprofen 400mg Tablets',
    productCode: 'IBU-TAB-400', lab: { name: 'Mumbai QC Central' }, status: 'completed',
    batchSize: 450000, unit: 'tablets', yieldActual: 426600, yieldExpected: 450000,
    yieldPercent: 94.8, startDate: '2024-10-14', endDate: '2024-10-19',
    releaseDate: '2024-10-28', operator: 'Meena Iyer', reviewedBy: 'Dr. Anjali Mehta',
    deviations: 1, remarks: 'Yield at lower end of acceptable range; tablet weight variation noted during compression. Blend re-mixing step added to SOP.',
  },
  {
    id: 'mock-batch-2408', batchCode: 'BT-ATV-2408', productName: 'Atorvastatin 10mg Tablets',
    productCode: 'ATV-TAB-010', lab: { name: 'Ahmedabad Stability Lab' }, status: 'completed',
    batchSize: 300000, unit: 'tablets', yieldActual: 295500, yieldExpected: 300000,
    yieldPercent: 98.5, startDate: '2024-11-20', endDate: '2024-11-25',
    releaseDate: '2024-12-05', operator: 'Vijay Sharma', reviewedBy: 'Dr. Preeti Joshi',
    deviations: 0, remarks: null,
  },
  // 2025
  {
    id: 'mock-batch-2501', batchCode: 'BT-AMX-2501', productName: 'Amoxicillin 500mg Capsules',
    productCode: 'AMX-CAP-500', lab: { name: 'Pune Formulation Lab' }, status: 'released',
    batchSize: 200000, unit: 'units', yieldActual: 197400, yieldExpected: 200000,
    yieldPercent: 98.7, startDate: '2025-03-01', endDate: '2025-03-05',
    releaseDate: '2025-03-12', operator: 'Suresh Patil', reviewedBy: 'Dr. Anjali Mehta',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2502', batchCode: 'BT-CIP-2502', productName: 'Ciprofloxacin 250mg Tablets',
    productCode: 'CIP-TAB-250', lab: { name: 'Mumbai QC Central' }, status: 'in_progress',
    batchSize: 500000, unit: 'tablets', yieldActual: 423000, yieldExpected: 500000,
    yieldPercent: 84.6, startDate: '2025-04-01', endDate: null,
    releaseDate: null, operator: 'Kavitha Nair', reviewedBy: null,
    deviations: 2, remarks: 'Coating stage in progress; deviations raised for temperature excursion. Yield tracking active.',
  },
  {
    id: 'mock-batch-2503', batchCode: 'BT-SAL-2503', productName: 'Sodium Chloride 0.9% Injection',
    productCode: 'SAL-INJ-090', lab: { name: 'Chennai Sterile Lab' }, status: 'quarantine',
    batchSize: 10000, unit: 'vials', yieldActual: 9850, yieldExpected: 10000,
    yieldPercent: 98.5, startDate: '2025-03-20', endDate: '2025-03-22',
    releaseDate: null, operator: 'Arun Menon', reviewedBy: 'Priya Nair',
    deviations: 1, remarks: 'Batch quarantined pending investigation of particulate matter observation during visual inspection.',
  },
  {
    id: 'mock-batch-2504', batchCode: 'BT-PCM-2504', productName: 'Paracetamol 650mg ER Tablets',
    productCode: 'PCM-TAB-650', lab: { name: 'Hyderabad API Lab' }, status: 'completed',
    batchSize: 1000000, unit: 'tablets', yieldActual: 987500, yieldExpected: 1000000,
    yieldPercent: 98.75, startDate: '2025-02-10', endDate: '2025-02-15',
    releaseDate: '2025-02-22', operator: 'Harish Reddy', reviewedBy: 'Dr. Meena Iyer',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2505', batchCode: 'BT-AZI-2505', productName: 'Azithromycin 500mg Tablets',
    productCode: 'AZI-TAB-500', lab: { name: 'Hyderabad API Lab' }, status: 'released',
    batchSize: 200000, unit: 'tablets', yieldActual: 199000, yieldExpected: 200000,
    yieldPercent: 99.5, startDate: '2025-01-14', endDate: '2025-01-18',
    releaseDate: '2025-01-27', operator: 'Harish Reddy', reviewedBy: 'Dr. Meena Iyer',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2506', batchCode: 'BT-PAN-2506', productName: 'Pantoprazole 40mg Tablets',
    productCode: 'PAN-TAB-040', lab: { name: 'Pune Formulation Lab' }, status: 'in_progress',
    batchSize: 400000, unit: 'tablets', yieldActual: 310000, yieldExpected: 400000,
    yieldPercent: 87.5, startDate: '2025-04-05', endDate: null,
    releaseDate: null, operator: 'Arjun Pillai', reviewedBy: null,
    deviations: 1, remarks: 'Enteric coating sub-process active. Yield below expectation due to coating weight gain variability; investigation in progress.',
  },
  {
    id: 'mock-batch-2507', batchCode: 'BT-PRD-2507', productName: 'Prednisolone 5mg Tablets',
    productCode: 'PRD-TAB-005', lab: { name: 'Mumbai QC Central' }, status: 'released',
    batchSize: 250000, unit: 'tablets', yieldActual: 247500, yieldExpected: 250000,
    yieldPercent: 99.0, startDate: '2025-02-24', endDate: '2025-02-28',
    releaseDate: '2025-03-10', operator: 'Meena Iyer', reviewedBy: 'Dr. Anjali Mehta',
    deviations: 0, remarks: null,
  },
  // 2026
  {
    id: 'mock-batch-2601', batchCode: 'BT-MET-2601', productName: 'Metformin 500mg Tablets',
    productCode: 'MET-TAB-500', lab: { name: 'Pune Formulation Lab' }, status: 'completed',
    batchSize: 650000, unit: 'tablets', yieldActual: 643500, yieldExpected: 650000,
    yieldPercent: 99.0, startDate: '2026-01-06', endDate: '2026-01-11',
    releaseDate: '2026-01-20', operator: 'Deepak Rao', reviewedBy: 'Ramesh Kulkarni',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2602', batchCode: 'BT-IBU-2602', productName: 'Ibuprofen 400mg Tablets',
    productCode: 'IBU-TAB-400', lab: { name: 'Ahmedabad Stability Lab' }, status: 'completed',
    batchSize: 480000, unit: 'tablets', yieldActual: 468240, yieldExpected: 480000,
    yieldPercent: 97.55, startDate: '2026-02-10', endDate: '2026-02-15',
    releaseDate: '2026-02-24', operator: 'Vijay Sharma', reviewedBy: 'Dr. Preeti Joshi',
    deviations: 0, remarks: null,
  },
  {
    id: 'mock-batch-2603', batchCode: 'BT-ATV-2603', productName: 'Atorvastatin 10mg Tablets',
    productCode: 'ATV-TAB-010', lab: { name: 'Pune Formulation Lab' }, status: 'completed',
    batchSize: 350000, unit: 'tablets', yieldActual: 347550, yieldExpected: 350000,
    yieldPercent: 99.3, startDate: '2026-03-03', endDate: '2026-03-07',
    releaseDate: '2026-03-18', operator: 'Sonal Desai', reviewedBy: 'Dr. Anjali Mehta',
    deviations: 0, remarks: null,
  },
];

export default function Bmr() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '');
  const [labId, setLabId] = useState('');
  const [dateRange, setDateRange] = useState('6m');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState<BatchRecord | null>(null);
  const [exporting, setExporting] = useState<string | null>(null);
  const [form, setForm] = useState({
    productName: '', productCode: '', labId: '', batchSize: '', unit: 'kg',
    yieldExpected: '', startDate: '', operator: '',
  });
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['batches', { search, status: statusFilter, page, labId, dateRange }],
    queryFn: () =>
      api.get('/batches', { params: { search, status: statusFilter, page, limit: 20, ...(labId && { labId }), range: dateRange } })
        .then((r) => r.data),
  });

  const { data: monthlyOutput, isLoading: outputLoading } = useQuery({
    queryKey: ['batch-monthly-output'],
    queryFn: () => api.get('/batches/monthly-output').then((r) => r.data.data),
    staleTime: 300000,
  });

  const { data: yieldTrend, isLoading: yieldLoading } = useQuery({
    queryKey: ['batch-yield-trend'],
    queryFn: () => api.get('/batches/yield-trend').then((r) => r.data.data),
    staleTime: 300000,
  });

  const { data: labs } = useQuery({
    queryKey: ['labs-all'],
    queryFn: () => api.get('/labs/all').then((r) => r.data.data),
  });

  const createMutation = useMutation({
    mutationFn: (payload: typeof form) => api.post('/batches', payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['batches'] });
      setShowCreate(false);
    },
  });

  const columns: Column<Record<string, unknown>>[] = [
    { key: 'batchCode', label: 'Batch ID', width: 'w-32',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.batchCode)}</span> },
    { key: 'productName', label: 'Product',
      render: (row) => (
        <div>
          <p className="font-medium text-text text-sm">{String(row.productName)}</p>
          <p className="text-xs font-mono text-text-dim">{String(row.productCode || '—')}</p>
        </div>
      )},
    { key: 'lab', label: 'Lab',
      render: (row) => {
        const lab = row.lab as { name: string } | null;
        return <span className="text-sm text-text">{lab?.name || '—'}</span>;
      }},
    { key: 'status', label: 'Status', render: (row) => <PillBadge value={String(row.status)} showDot /> },
    { key: 'batchSize', label: 'Batch Size',
      render: (row) => (
        <span className="text-sm font-mono">{Number(row.batchSize).toLocaleString()} {String(row.unit)}</span>
      )},
    { key: 'yieldPercent', label: 'Yield', align: 'center',
      render: (row) => {
        const yld = Number(row.yieldPercent);
        return (
          <div className="w-20">
            <p className={`text-xs font-bold text-center mb-0.5 ${yld >= 95 ? 'text-risk-low' : yld >= 90 ? 'text-risk-medium' : 'text-risk-high'}`}>
              {yld.toFixed(1)}%
            </p>
            <ProgressBar value={yld} size="sm" />
          </div>
        );
      }},
    { key: 'startDate', label: 'Start Date',
      render: (row) => <span className="text-xs font-mono">{format(new Date(String(row.startDate)), 'd MMM yyyy')}</span> },
    { key: 'deviations', label: 'Dev', align: 'center',
      render: (row) => {
        const n = Number(row.deviations);
        return n > 0
          ? <span className="text-xs font-bold text-risk-high bg-risk-high/10 px-2 py-0.5 rounded-full">{n}</span>
          : <span className="text-xs text-text-dim">—</span>;
      }},
  ];

  const handleExportBatch = (id: string, fmt: 'pdf' | 'xlsx') => {
    const b = selectedBatch;
    if (!b) return;
    const fields: [string, string | number][] = [
      ['Batch Code',      b.batchCode],
      ['Product',         b.productName],
      ['Product Code',    b.productCode ?? '—'],
      ['Lab',             b.lab?.name ?? '—'],
      ['Status',          b.status],
      ['Batch Size',      `${b.batchSize.toLocaleString()} ${b.unit}`],
      ['Yield Expected',  `${b.yieldExpected} ${b.unit}`],
      ['Yield Actual',    `${b.yieldActual} ${b.unit}`],
      ['Yield %',         `${b.yieldPercent?.toFixed(2)}%`],
      ['Operator',        b.operator ?? '—'],
      ['Start Date',      new Date(b.startDate).toLocaleDateString('en-IN')],
      ['End Date',        b.endDate ? new Date(b.endDate).toLocaleDateString('en-IN') : '—'],
      ['Deviations',      b.deviations ?? 0],
    ];
    if (fmt === 'pdf') exportRecordPDF(`Batch Manufacturing Record — ${b.batchCode}`, b.productName, fields);
    else exportRecordXLSX(`batch-${id}`, fields);
  };

  const handleExport = () => {
    const rows = data?.data as BatchRecord[] || [];
    const csv = ['Batch Code,Product,Lab,Status,Batch Size,Unit,Yield %,Start Date'].concat(
      rows.map((b) => `${b.batchCode},"${b.productName}","${b.lab?.name}",${b.status},${b.batchSize},${b.unit},${b.yieldPercent},${b.startDate}`)
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'batch-records.csv'; a.click();
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Batch Manufacturing Records"
        subtitle={`${data?.meta?.total || 0} batch records`}
        actions={
          <>
            <ALCOABadge />
            <button onClick={handleExport} className="btn-secondary"><Download size={13} /> Export</button>
            <CanAccess roles={['admin', 'qa_director', 'lab_head']}>
              <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus size={13} /> New Batch</button>
            </CanAccess>
          </>
        }
      />

      <FilterBar
        labId={labId} onLabChange={(id) => { setLabId(id); setPage(1); }}
        dateRange={dateRange} onDateRangeChange={(r) => { setDateRange(r); setPage(1); }}
      />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {outputLoading ? <ChartSkeleton height={200} /> : (
          <ChartCard title="Monthly Batch Output" subtitle="Batches produced per month" height={200}>
            <ResponsiveContainer>
              <BarChart data={monthlyOutput || []} margin={{ top: 5, right: 20, bottom: 5, left: 0 }} barSize={18}>
                <CartesianGrid strokeDasharray="3 3" stroke="#C6D8D8" strokeOpacity={0.5} />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6899A0' }} />
                <YAxis tick={{ fontSize: 10, fill: '#6899A0' }} width={25} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Bar dataKey="released" name="Released" stackId="a" fill="#b07d1a" radius={[0, 0, 0, 0]} />
                <Bar dataKey="onHold" name="On Hold" stackId="a" fill="#DC2626" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {yieldLoading ? <ChartSkeleton height={200} /> : (
          <ChartCard title="Yield Trend" subtitle="Average yield % per month" height={200}>
            <ResponsiveContainer>
              <AreaChart data={yieldTrend || []} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <defs>
                  <linearGradient id="yieldGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#b07d1a" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#b07d1a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#C6D8D8" strokeOpacity={0.5} />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6899A0' }} />
                <YAxis domain={[85, 100]} tick={{ fontSize: 10, fill: '#6899A0' }} width={35} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => [`${Number(v).toFixed(1)}%`, 'Avg Yield']} />
                <Area type="monotone" dataKey="avgYield" stroke="#b07d1a" strokeWidth={2} fill="url(#yieldGrad)" name="Avg Yield %" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input placeholder="Search batches..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="input pl-8" />
        </div>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Status</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="released">Released</option>
          <option value="rejected">Rejected</option>
          <option value="quarantine">Quarantine</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={((data?.data && data.data.length > 0) ? data.data : MOCK_BATCHES) as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelectedBatch(row as unknown as BatchRecord)}
        total={data?.meta?.total}
        page={page}
        pageSize={20}
        onPageChange={setPage}
      />

      {/* Detail Drawer */}
      <SideDrawer isOpen={!!selectedBatch} onClose={() => setSelectedBatch(null)}
        title={selectedBatch?.batchCode || ''} subtitle={selectedBatch?.productName} width={460}>
        {selectedBatch && (
          <div className="space-y-5">
            <div className="flex items-center gap-2">
              <PillBadge value={selectedBatch.status} showDot />
              {selectedBatch.deviations > 0 && (
                <span className="text-xs font-semibold text-risk-high bg-risk-high/10 px-2 py-0.5 rounded-full">
                  {selectedBatch.deviations} deviation{selectedBatch.deviations > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Product Code', value: selectedBatch.productCode || '—' },
                { label: 'Lab', value: selectedBatch.lab?.name || '—' },
                { label: 'Batch Size', value: `${selectedBatch.batchSize.toLocaleString()} ${selectedBatch.unit}` },
                { label: 'Yield Expected', value: `${selectedBatch.yieldExpected} ${selectedBatch.unit}` },
                { label: 'Yield Actual', value: `${selectedBatch.yieldActual} ${selectedBatch.unit}` },
                { label: 'Operator', value: selectedBatch.operator || '—' },
                { label: 'Start Date', value: format(new Date(selectedBatch.startDate), 'd MMM yyyy') },
                { label: 'End Date', value: selectedBatch.endDate ? format(new Date(selectedBatch.endDate), 'd MMM yyyy') : '—' },
              ].map((item) => (
                <div key={item.label} className="p-3 bg-bg rounded-lg">
                  <p className="text-xs text-text-muted mb-0.5">{item.label}</p>
                  <p className="text-sm font-medium text-text">{item.value}</p>
                </div>
              ))}
            </div>
            <div className="p-3 bg-bg rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs text-text-muted">Yield</p>
                <p className={`text-sm font-bold ${selectedBatch.yieldPercent >= 95 ? 'text-risk-low' : selectedBatch.yieldPercent >= 90 ? 'text-risk-medium' : 'text-risk-high'}`}>
                  {selectedBatch.yieldPercent.toFixed(2)}%
                </p>
              </div>
              <ProgressBar value={selectedBatch.yieldPercent} size="md" />
            </div>
            {selectedBatch.remarks && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-1">Remarks</p>
                <p className="text-sm text-text leading-relaxed">{selectedBatch.remarks}</p>
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button onClick={() => handleExportBatch(selectedBatch.id, 'pdf')} disabled={!!exporting} className="btn-secondary flex-1 text-xs">
                {exporting === 'pdf' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} PDF
              </button>
              <button onClick={() => handleExportBatch(selectedBatch.id, 'xlsx')} disabled={!!exporting} className="btn-secondary flex-1 text-xs">
                {exporting === 'xlsx' ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Excel
              </button>
            </div>
          </div>
        )}
      </SideDrawer>

      {/* Create Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="New Batch Record"
        size="md"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            <button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending} className="btn-primary">
              {createMutation.isPending ? 'Creating...' : 'Create Batch'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Product Name *</label>
            <input className="input" value={form.productName} onChange={(e) => setForm((f) => ({ ...f, productName: e.target.value }))} />
          </div>
          <div>
            <label className="label">Product Code</label>
            <input className="input" value={form.productCode} onChange={(e) => setForm((f) => ({ ...f, productCode: e.target.value }))} />
          </div>
          <div>
            <label className="label">Lab *</label>
            <select className="select" value={form.labId} onChange={(e) => setForm((f) => ({ ...f, labId: e.target.value }))}>
              <option value="">Select lab...</option>
              {(labs || []).map((l: { id: string; name: string }) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Batch Size *</label>
            <input type="number" className="input" value={form.batchSize} onChange={(e) => setForm((f) => ({ ...f, batchSize: e.target.value }))} />
          </div>
          <div>
            <label className="label">Unit</label>
            <select className="select" value={form.unit} onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}>
              {['kg', 'L', 'units', 'vials', 'tablets'].map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Expected Yield</label>
            <input type="number" className="input" value={form.yieldExpected} onChange={(e) => setForm((f) => ({ ...f, yieldExpected: e.target.value }))} />
          </div>
          <div>
            <label className="label">Start Date *</label>
            <input type="date" className="input" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))} />
          </div>
          <div className="col-span-2">
            <label className="label">Operator</label>
            <input className="input" value={form.operator} onChange={(e) => setForm((f) => ({ ...f, operator: e.target.value }))} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
